import { PROJECT_TYPE_LABEL } from '@/components/composites/status-badge'
import type { ProjectTypeKey } from '@/types/entities'

/**
 * The engagement taxonomy, in the order it is offered.
 *
 * The labels live with the other domain labels in `composites/status-badge.tsx`; only the
 * ordering and the one-line hints are here, because they are a property of the picker
 * rather than of the value.
 */
export const PROJECT_TYPES: Array<{ key: ProjectTypeKey; hint: string }> = [
  { key: 'pre_sales', hint: 'Discovery, demos and proposals before a deal' },
  { key: 'pilot', hint: 'Proving it out — tied to revenue, so it is highlighted and tracked on the Pilots page' },
  { key: 'implementation', hint: 'Building the agreed scope' },
  { key: 'cost_improvement', hint: 'Making an existing engagement cheaper to run' },
  { key: 'hypercare', hint: 'Support right after go-live' },
  { key: 'post_sales', hint: 'Ongoing support once things are steady' },
  { key: 'additional_requirements', hint: 'Asks beyond the agreed scope' },
  { key: 'internal', hint: 'No client — our own work' },
]

export const PROJECT_TYPE_KEYS: ProjectTypeKey[] = PROJECT_TYPES.map((t) => t.key)

/**
 * What a NEW project can be (handbook: Client project lifecycle). A client engagement is one
 * Client Project for the life of the account, opened with its own form; everything else is
 * Internal. The older engagement types above stay for projects opened before, and for editing them.
 */
export const NEW_PROJECT_TYPES: ProjectTypeKey[] = ['internal']

/**
 * Engagement phases that must name a client.
 *
 * Mirrors `assertProjectClientRule` on the backend, which is the authority — this copy only
 * exists so the form can say what is wrong before a round trip. `pre_sales` is deliberately
 * absent (a prospect is usually not a client record yet) and so is
 * `additional_requirements`, where an ask can come from a client or from inside.
 */
export const CLIENT_PROJECT_TYPES: ProjectTypeKey[] = [
  'client',
  'pilot',
  'implementation',
  'cost_improvement',
  'hypercare',
  'post_sales',
]

/** Guidance only — the backend re-checks and its message is what gets shown on failure. */
export function clientRuleHint(
  type: ProjectTypeKey | '',
  clientId: string | null,
): string | null {
  if (!type) return null
  if (CLIENT_PROJECT_TYPES.includes(type) && !clientId) {
    return `A ${PROJECT_TYPE_LABEL[type]} project belongs to a client. Pick one, or use Internal for work that has none.`
  }
  if (type === 'internal' && clientId) {
    return 'An Internal project has no client. Clear the client, or pick a client project type.'
  }
  return null
}
